import React, { useEffect, useRef } from 'react';
import theme from '../../config/theme';
import { IoTrashOutline, IoClose } from 'react-icons/io5';

// Delete Dialog Props interface
export interface DeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string | React.ReactNode;
  className?: string;
}

// Delete Dialog Component
const DeleteDialog: React.FC<DeleteDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Delete Rich Menu',
  message,
  className = '',
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Close dialog on escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Close dialog when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const defaultMessage = (
    <>
      Are you sure you want to delete this?
      <br />
      This action cannot be undone.
    </>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        ref={dialogRef}
        className={`bg-white rounded-lg shadow-xl w-full max-w-md mx-auto ${className}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-dialog-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-0" style={{ borderColor: theme.color.light300 }}>
          <div className="flex items-center gap-4">
            {/* Icon with background circle */}
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: theme.color.error + '20' }}
            >
              {React.createElement(IoTrashOutline as any, {
                className: "w-5 h-5",
                style: { color: theme.color.error }
              })}
            </div>
            <h2 id="delete-dialog-title" className="text-xl font-medium" style={{ color: theme.color.gray500 }}>
              {title}
            </h2>
          </div>
          <button
            type="button"
            className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200"
            onClick={onClose}
            aria-label="Close dialog"
            style={{ color: theme.color.gray200 }}
          >
            {React.createElement(IoClose as any, { className: "h-6 w-6" })}
          </button>
        </div>

        {/* Message */}
        <div className="px-6 py-4 ml-14 font-light" style={{ color: theme.color.gray500 }}>
          {message || defaultMessage}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 px-6 pt-4 pb-6" style={{ borderColor: theme.color.light300 }}>
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 rounded-lg border text-sm font-medium transition-all"
            style={{
              borderColor: theme.color.light500,
              backgroundColor: theme.color.light100,
              color: theme.color.gray500
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-6 py-2 rounded-lg border-0 text-sm font-medium text-white transition-all"
            style={{
              backgroundColor: theme.color.error,
              color: theme.color.light100
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteDialog;

